/**
 * STAFFED — Tenant-aware date & time helpers (canonical).
 *
 * Every date and time shown in the app renders in the *company's* operating
 * timezone, NOT the viewer's browser or the server. The tenant timezone is
 * a single module-level variable set once at app boot (after we fetch
 * /company/settings) via `setTenantTimezone(tz)`.
 *
 * Two hard rules baked into these helpers:
 *
 *   1. UTC ISO timestamps (e.g. "2026-01-15T12:00:00Z") are converted to
 *      the tenant zone for display using Intl.DateTimeFormat with the
 *      explicit `timeZone` option.
 *
 *   2. Date-only fields (e.g. "2026-11-01") are NEVER passed through
 *      `new Date(...)`. We split the string and format directly so they
 *      cannot shift a day in negative-offset locales (the off-by-one bug
 *      we just fixed on the print brief).
 *
 * Storage is untouched. Timestamps remain UTC ISO; event dates remain
 * YYYY-MM-DD. Only the display layer changes.
 */

// ---------------------------------------------------------------------------
// Supported IANA US timezones — the dropdown source-of-truth.
// Mirror this list on the backend if you add a zone here.
// `abbr` is the short code shown on the Command Deck "now" pill so the
// admin can verify at a glance which zone every render is anchored to.
// ---------------------------------------------------------------------------
export const US_TIMEZONES = [
  { value: 'America/New_York',    label: 'Eastern Time (ET)',  abbr: 'ET'  },
  { value: 'America/Chicago',     label: 'Central Time (CT)',  abbr: 'CT'  },
  { value: 'America/Denver',      label: 'Mountain Time (MT)', abbr: 'MT'  },
  { value: 'America/Phoenix',     label: 'Arizona (no DST)',   abbr: 'MST' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)',  abbr: 'PT'  },
  { value: 'America/Anchorage',   label: 'Alaska Time (AKT)',  abbr: 'AKT' },
  { value: 'Pacific/Honolulu',    label: 'Hawaii Time (HT)',   abbr: 'HT'  },
];

export const DEFAULT_TENANT_TIMEZONE = 'America/New_York';

const VALID_TZ_SET = new Set(US_TIMEZONES.map((z) => z.value));

// ---------------------------------------------------------------------------
// US state → primary IANA timezone. Used to pre-select the dropdown when
// the company already has a state on file. Only one zone per state — the
// primary/most-populous when a state is split (e.g. Florida is Eastern
// even though the panhandle is Central). Tenants can override.
// ---------------------------------------------------------------------------
export const US_STATE_TO_TIMEZONE = {
  AL: 'America/Chicago',        AK: 'America/Anchorage',
  AZ: 'America/Phoenix',        AR: 'America/Chicago',
  CA: 'America/Los_Angeles',    CO: 'America/Denver',
  CT: 'America/New_York',       DE: 'America/New_York',
  DC: 'America/New_York',       FL: 'America/New_York',
  GA: 'America/New_York',       HI: 'Pacific/Honolulu',
  ID: 'America/Denver',         IL: 'America/Chicago',
  IN: 'America/New_York',       IA: 'America/Chicago',
  KS: 'America/Chicago',        KY: 'America/New_York',
  LA: 'America/Chicago',        ME: 'America/New_York',
  MD: 'America/New_York',       MA: 'America/New_York',
  MI: 'America/New_York',       MN: 'America/Chicago',
  MS: 'America/Chicago',        MO: 'America/Chicago',
  MT: 'America/Denver',         NE: 'America/Chicago',
  NV: 'America/Los_Angeles',    NH: 'America/New_York',
  NJ: 'America/New_York',       NM: 'America/Denver',
  NY: 'America/New_York',       NC: 'America/New_York',
  ND: 'America/Chicago',        OH: 'America/New_York',
  OK: 'America/Chicago',        OR: 'America/Los_Angeles',
  PA: 'America/New_York',       RI: 'America/New_York',
  SC: 'America/New_York',       SD: 'America/Chicago',
  TN: 'America/Chicago',        TX: 'America/Chicago',
  UT: 'America/Denver',         VT: 'America/New_York',
  VA: 'America/New_York',       WA: 'America/Los_Angeles',
  WV: 'America/New_York',       WI: 'America/Chicago',
  WY: 'America/Denver',
};

/**
 * Map a free-form state string (e.g. "California", "ca", "CA") to one of
 * the seven supported tenant zones, or null if no clean match exists.
 */
export const timezoneForState = (state) => {
  if (!state || typeof state !== 'string') return null;
  const code = state.trim().toUpperCase();
  if (US_STATE_TO_TIMEZONE[code]) return US_STATE_TO_TIMEZONE[code];

  const STATE_NAMES = {
    ALABAMA: 'AL', ALASKA: 'AK', ARIZONA: 'AZ', ARKANSAS: 'AR',
    CALIFORNIA: 'CA', COLORADO: 'CO', CONNECTICUT: 'CT', DELAWARE: 'DE',
    'DISTRICT OF COLUMBIA': 'DC', FLORIDA: 'FL', GEORGIA: 'GA', HAWAII: 'HI',
    IDAHO: 'ID', ILLINOIS: 'IL', INDIANA: 'IN', IOWA: 'IA',
    KANSAS: 'KS', KENTUCKY: 'KY', LOUISIANA: 'LA', MAINE: 'ME',
    MARYLAND: 'MD', MASSACHUSETTS: 'MA', MICHIGAN: 'MI', MINNESOTA: 'MN',
    MISSISSIPPI: 'MS', MISSOURI: 'MO', MONTANA: 'MT', NEBRASKA: 'NE',
    NEVADA: 'NV', 'NEW HAMPSHIRE': 'NH', 'NEW JERSEY': 'NJ', 'NEW MEXICO': 'NM',
    'NEW YORK': 'NY', 'NORTH CAROLINA': 'NC', 'NORTH DAKOTA': 'ND', OHIO: 'OH',
    OKLAHOMA: 'OK', OREGON: 'OR', PENNSYLVANIA: 'PA', 'RHODE ISLAND': 'RI',
    'SOUTH CAROLINA': 'SC', 'SOUTH DAKOTA': 'SD', TENNESSEE: 'TN', TEXAS: 'TX',
    UTAH: 'UT', VERMONT: 'VT', VIRGINIA: 'VA', WASHINGTON: 'WA',
    'WEST VIRGINIA': 'WV', WISCONSIN: 'WI', WYOMING: 'WY',
  };
  const named = STATE_NAMES[code];
  return named ? US_STATE_TO_TIMEZONE[named] : null;
};

// ---------------------------------------------------------------------------
// Tenant timezone — module-level. Set at app boot from /company/settings.
// ---------------------------------------------------------------------------
let _tenantTimezone = DEFAULT_TENANT_TIMEZONE;

/**
 * Set the active tenant timezone. Silently ignores invalid values so a
 * malformed company-settings response can never break date rendering.
 */
export const setTenantTimezone = (tz) => {
  if (typeof tz === 'string' && VALID_TZ_SET.has(tz)) {
    _tenantTimezone = tz;
  }
};

export const getTenantTimezone = () => _tenantTimezone;

/**
 * Short abbreviation for the active tenant timezone — "ET", "PT", etc.
 * Used by the Command Deck "now" pill to give the admin a constant
 * visual cue of which zone every date/time render is anchored to.
 */
export const getTenantTimezoneAbbr = () => {
  const found = US_TIMEZONES.find((z) => z.value === _tenantTimezone);
  return found ? found.abbr : 'ET';
};

/** Test-only: reset to default. */
export const __resetTenantTimezoneForTests = () => {
  _tenantTimezone = DEFAULT_TENANT_TIMEZONE;
};

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
// A midnight-UTC ISO ("YYYY-MM-DDT00:00:00" ± zone suffix) is almost
// certainly a *calendar date* stored as an instant — a real Stripe /
// webhook payment stamps a wall-clock time, virtually never lands
// exactly at midnight UTC. Historical record-manual writes (before the
// Aug 8 date-only fix) all landed this way, and rendering them in a
// US timezone slides the calendar back a day. Treat this shape as a
// date-only value on the render path so historical data reads the
// admin-entered day even without running the backfill endpoint.
const MIDNIGHT_UTC_ISO_RE = /^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.0+)?(?:Z|\+00:?00)?$/i;

const splitDateOnly = (s) => {
  const m = DATE_ONLY_RE.exec(s);
  if (!m) return null;
  return { year: m[1], month: m[2], day: m[3] };
};

const splitMidnightUtc = (s) => {
  const m = MIDNIGHT_UTC_ISO_RE.exec(s);
  if (!m) return null;
  return { year: m[1], month: m[2], day: m[3] };
};

const padTwo = (n) => String(n).padStart(2, '0');

// ---------------------------------------------------------------------------
// formatDate(input, { timezone? })  →  "MM/DD/YYYY"
//
// - Date-only "YYYY-MM-DD"  → split and pad, NEVER through new Date()
// - ISO timestamp           → convert to tenant zone, then MM/DD/YYYY
// - Date instance           → format in tenant zone
// - falsy / unparseable     → '' (so callers can branch on truthiness)
// ---------------------------------------------------------------------------
export const formatDate = (input, opts = {}) => {
  if (input === null || input === undefined || input === '') return '';
  const tz = opts.timezone || _tenantTimezone;

  if (typeof input === 'string') {
    const trimmed = input.trim();
    const dateOnly = splitDateOnly(trimmed);
    if (dateOnly) return `${dateOnly.month}/${dateOnly.day}/${dateOnly.year}`;
    // Historical record-manual data (pre 2026-08-08) stored calendar
    // dates as midnight-UTC instants. Detect and treat as a date so
    // an Aug 7 recording doesn't render as Aug 6 in America/New_York.
    const midUtc = splitMidnightUtc(trimmed);
    if (midUtc) return `${midUtc.month}/${midUtc.day}/${midUtc.year}`;
  }

  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return '';

  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  } catch {
    return '';
  }
};

// ---------------------------------------------------------------------------
// formatTime(input, { timezone? }) → "5:00 PM" (12-hour, US locale)
//
// - "17:00" or "17:00:00"   → wall-clock string, formatted as-is (no tz)
// - "h:mm AM/PM"            → returned unchanged
// - ISO timestamp / Date    → converted to tenant zone, then h:mm A
// ---------------------------------------------------------------------------
export const formatTime = (input, opts = {}) => {
  if (input === null || input === undefined || input === '') return '';
  const tz = opts.timezone || _tenantTimezone;

  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (/AM|PM/i.test(trimmed)) return trimmed;

    // 24-hour wall-clock ("17:00" or "17:00:00") — no zone conversion.
    const wall = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(trimmed);
    if (wall) {
      const hour = parseInt(wall[1], 10);
      const minutes = wall[2];
      if (Number.isNaN(hour) || hour < 0 || hour > 23) return trimmed;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const h12 = hour % 12 || 12;
      return `${h12}:${minutes} ${ampm}`;
    }
  }

  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return '';

  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  } catch {
    return '';
  }
};

// ---------------------------------------------------------------------------
// formatDateTime(input) → "MM/DD/YYYY at h:mm A"
//
// Date-only inputs (no time component) return just the date — there is
// no meaningful "at …" suffix when the caller never supplied a time.
// ---------------------------------------------------------------------------
export const formatDateTime = (input, opts = {}) => {
  if (input === null || input === undefined || input === '') return '';
  const datePart = formatDate(input, opts);
  if (!datePart) return '';
  if (typeof input === 'string' && splitDateOnly(input.trim())) {
    return datePart;
  }
  const timePart = formatTime(input, opts);
  if (!timePart) return datePart;
  return `${datePart} at ${timePart}`;
};

// ---------------------------------------------------------------------------
// Convenience extras — same tenant-zone contract.
// ---------------------------------------------------------------------------

/** Day of week for the input ("Mon", "Tue", …) in the tenant zone. */
export const formatWeekday = (input, opts = {}) => {
  if (input === null || input === undefined || input === '') return '';
  const tz = opts.timezone || _tenantTimezone;
  const long = !!opts.long;

  if (typeof input === 'string') {
    const dateOnly = splitDateOnly(input.trim());
    if (dateOnly) {
      // Build a Date at noon UTC on that date so timezone math never
      // shifts the calendar day. We then format with the tenant zone.
      const utcNoon = new Date(`${dateOnly.year}-${dateOnly.month}-${dateOnly.day}T12:00:00Z`);
      return new Intl.DateTimeFormat('en-US', {
        timeZone: 'UTC',
        weekday: long ? 'long' : 'short',
      }).format(utcNoon);
    }
  }

  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    weekday: long ? 'long' : 'short',
  }).format(date);
};

/** "Mon, 11/01/2026" */
export const formatDateWithDay = (input, opts = {}) => {
  const datePart = formatDate(input, opts);
  if (!datePart) return '';
  const dow = formatWeekday(input, opts);
  return dow ? `${dow}, ${datePart}` : datePart;
};

/** "Monday, 11/01/2026" */
export const formatDateLong = (input, opts = {}) => {
  const datePart = formatDate(input, opts);
  if (!datePart) return '';
  const dow = formatWeekday(input, { ...opts, long: true });
  return dow ? `${dow}, ${datePart}` : datePart;
};

/**
 * Event start–end time range. Both inputs are usually wall-clock 24h
 * strings ("17:00") authored against the tenant zone, so no conversion
 * is required. Returns "Time TBD" when either bound is missing.
 */
export const formatTimeRange = (startTime, endTime, opts = {}) => {
  if (!startTime || !endTime) return 'Time TBD';
  return `${formatTime(startTime, opts)} – ${formatTime(endTime, opts)}`;
};

// ---------------------------------------------------------------------------
// Legacy aliases — preserved so existing imports keep working while
// the codebase finishes migrating onto lib/datetime.js.
// ---------------------------------------------------------------------------
export const formatTimestamp = formatDateTime;
export const formatDateShort = formatDate;

/**
 * Timezone-safe MM/DD/YYYY from an ISO-shaped string. Kept as a named
 * export because several print-brief tests pin its exact contract.
 */
export const formatDateMDY = (isoStr) => {
  if (!isoStr || typeof isoStr !== 'string') return '';
  const datePart = isoStr.split('T')[0];
  const parts = datePart.split('-');
  if (parts.length !== 3) return isoStr;
  const [y, m, d] = parts;
  if (!/^\d{4}$/.test(y) || !/^\d{1,2}$/.test(m) || !/^\d{1,2}$/.test(d)) return isoStr;
  return `${padTwo(m)}/${padTwo(d)}/${y}`;
};

/**
 * Today's calendar day in the tenant zone as a sortable "YYYY-MM-DD".
 *
 * The canonical answer for date-only comparisons. A date-only field has no
 * time, so "is it past" can only honestly be a CALENDAR-DAY question — the
 * current day is never past, only the day after is. Comparing instants is
 * wrong twice over: `new Date('2026-08-08')` is 8pm the previous day in ET,
 * and even a correct local midnight is already behind "now" from 00:01 on
 * the day itself. Compare day strings instead:
 *
 *     const isPast = eventDay < tenantTodayString();
 *
 * 'en-CA' is used because it formats as YYYY-MM-DD, which sorts
 * lexicographically as a date.
 */
export const tenantTodayString = (now = new Date(), tz = _tenantTimezone) => {
  try {
    return now.toLocaleDateString('en-CA', { timeZone: tz });
  } catch {
    return now.toLocaleDateString('en-CA');
  }
};

/**
 * Sortable "YYYY-MM-DD" calendar day for a stored date value.
 *
 * Policy (must match the backend `day_key_for_tenant` exactly):
 *   • date-only "YYYY-MM-DD"          → the stored day, NO conversion.
 *   • exact midnight UTC (…T00:00:00Z) → the stored day, NO conversion.
 *     These are calendar dates that were stored as instants. Converting one
 *     into a negative-offset zone yields the PREVIOUS day, which is the bug,
 *     not the fix.
 *   • any real timestamp               → the day in the tenant zone.
 *   • missing / malformed              → '' so callers can fail CLOSED.
 *
 * Pair with `tenantTodayString()` for the other side of the comparison:
 *     const overdue = Boolean(dueDay) && dueDay < tenantTodayString();
 */
export const tenantDayString = (input, opts = {}) => {
  if (input === null || input === undefined || input === '') return '';
  const tz = opts.timezone || _tenantTimezone;

  if (typeof input === 'string') {
    const trimmed = input.trim();
    const dateOnly = splitDateOnly(trimmed);
    if (dateOnly) return `${dateOnly.year}-${dateOnly.month}-${dateOnly.day}`;
    const midUtc = splitMidnightUtc(trimmed);
    if (midUtc) return `${midUtc.year}-${midUtc.month}-${midUtc.day}`;
    // Only a full ISO datetime may go through Date parsing. A partial value
    // like "2026-08" is accepted by `new Date` and silently becomes Aug 1 —
    // for money that is guessing a date, so refuse it and fail CLOSED.
    // ⚠️ Load-bearing invariant: MIDNIGHT_UTC_ISO_RE above must stay in
    // lockstep with the `hour == 0 and minute == 0 and second == 0 and
    // microsecond == 0` branch in backend/utils/tenant_datetime.py
    // ::day_key_for_tenant. The shared contract table
    // src/lib/__fixtures__/dayKeyCases.json fails one of the two suites if
    // they ever drift.
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(trimmed)) return '';
  }

  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return date.toLocaleDateString('en-CA', { timeZone: tz });
  } catch {
    return '';
  }
};

/**
 * The tenant's current calendar day as a LOCAL-midnight Date.
 *
 * Drop-in replacement for the `new Date(); d.setHours(0,0,0,0)` idiom, which
 * anchors "today" to the VIEWER's browser zone. A staffer in Chicago, a client
 * in California, or the owner travelling read a different calendar day than the
 * company operates on -- and the backend (utils/tenant_datetime.py) always
 * answers in the TENANT zone, so the two sides disagreed.
 *
 * Returns a local-midnight Date so existing `evDay < today` comparisons against
 * `parseLocalDate(...)` results keep working unchanged.
 */
export const tenantTodayMidnight = (now = new Date(), tz = _tenantTimezone) => {
  const [y, m, d] = tenantTodayString(now, tz).split('-').map(Number);
  if (!y || !m || !d) {
    const fallback = new Date();
    fallback.setHours(0, 0, 0, 0);
    return fallback;
  }
  return new Date(y, m - 1, d);
};
/** Parse a date-only or ISO string into a Date in local time (legacy). */
export const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const s = String(dateStr).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [m, d, y] = s.split('/').map(Number);
    return new Date(y, m - 1, d);
  }
  const dateOnly = splitDateOnly(s);
  if (dateOnly) {
    return new Date(Number(dateOnly.year), Number(dateOnly.month) - 1, Number(dateOnly.day));
  }
  const date = new Date(s);
  return Number.isNaN(date.getTime()) ? null : date;
};
